class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      if (event.target.closest?.('[data-rfrm-cart-close]')) this.close();
    });
    this.setHeaderCartIconAccessibility();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.rfrmCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.updateRfrmCartHeader(event?.cartData);
      });
    }

    this.updateRfrmCartHeader();
  }

  disconnectedCallback() {
    if (this.rfrmCartUpdateUnsubscriber) this.rfrmCartUpdateUnsubscriber();
    if (this.rfrmReservationInterval) window.clearInterval(this.rfrmReservationInterval);
  }

  updateRfrmCartHeader(cartData) {
    const header = this.querySelector('[data-rfrm-cart-header]');
    if (!header) return;

    const goalOne = Number(header.dataset.goalOne || 0);
    const goalTwo = Number(header.dataset.goalTwo || 0);
    const currencySymbol = header.dataset.currencySymbol || '';
    const deliveryLabel = (header.dataset.deliveryLabel || 'SHIPPING').toUpperCase();
    const totalInCents = Number(cartData?.total_price ?? header.dataset.cartTotal ?? 0);
    const itemCount = Number(cartData?.item_count ?? header.dataset.cartCount ?? 0);
    const total = totalInCents / 100;

    header.dataset.cartTotal = String(totalInCents);
    header.dataset.cartCount = String(itemCount);

    const count = header.querySelector('[data-rfrm-cart-count]');
    const deliveryText = header.querySelector('[data-rfrm-cart-delivery]');
    const progress = header.querySelector('[data-rfrm-cart-progress]');
    const milestoneOne = header.querySelector('[data-rfrm-cart-milestone="one"]');
    const milestoneTwo = header.querySelector('[data-rfrm-cart-milestone="two"]');

    if (count) count.textContent = String(itemCount);

    const progressPercent = goalTwo > 0 ? Math.min(100, Math.max(0, (total / goalTwo) * 100)) : 0;
    if (progress) progress.style.width = `${progressPercent}%`;

    const formatDifference = (value) => Number(value).toFixed(2);

    if (total < goalOne) {
      const difference = formatDifference(goalOne - total);
      if (deliveryText) {
        deliveryText.innerHTML = `ADD <b>${currencySymbol}${difference}</b> FOR <b>FREE ${deliveryLabel}</b>`;
      }
      milestoneOne?.classList.remove('active');
      milestoneTwo?.classList.remove('active');
    } else if (total < goalTwo) {
      const difference = formatDifference(goalTwo - total);
      if (deliveryText) {
        deliveryText.innerHTML = `FREE ${deliveryLabel} UNLOCKED — <b>${currencySymbol}${difference}</b> TO <b>NEXT TIER</b>`;
      }
      milestoneOne?.classList.add('active');
      milestoneTwo?.classList.remove('active');
    } else {
      if (deliveryText) deliveryText.innerHTML = '<b>TOP TIER SECURED</b>';
      milestoneOne?.classList.add('active');
      milestoneTwo?.classList.add('active');
    }

    this.startRfrmReservationTimer(header);
  }

  startRfrmReservationTimer(header) {
    const timer = header.querySelector('[data-rfrm-cart-timer]');
    if (!timer) return;

    const market = header.dataset.market || 'row';
    const storageKey = `rfrm_cart_reservation_${market}_end`;
    const now = Date.now();
    let endTimestamp = 0;

    try {
      endTimestamp = Number(window.sessionStorage.getItem(storageKey) || 0);
      if (!endTimestamp) {
        endTimestamp = now + 60 * 60 * 1000;
        window.sessionStorage.setItem(storageKey, String(endTimestamp));
      }
    } catch (error) {
      endTimestamp = this.rfrmReservationEndTimestamp || now + 60 * 60 * 1000;
      this.rfrmReservationEndTimestamp = endTimestamp;
    }

    if (this.rfrmReservationInterval) window.clearInterval(this.rfrmReservationInterval);

    const renderTimer = () => {
      const currentTimer = this.querySelector('[data-rfrm-cart-timer]');
      if (!currentTimer) return;

      const remainingSeconds = Math.max(0, Math.floor((endTimestamp - Date.now()) / 1000));
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      currentTimer.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      if (remainingSeconds === 0 && this.rfrmReservationInterval) {
        window.clearInterval(this.rfrmReservationInterval);
        this.rfrmReservationInterval = null;
      }
    };

    renderTimer();
    this.rfrmReservationInterval = window.setInterval(renderTimer, 1000);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.removeAttribute('aria-hidden');
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active', 'animate');
    removeTrapFocus(this.activeElement);
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.updateRfrmCartHeader(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
